//
//  Workstreams.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: September 23, 2026
//  Author: Bob Keathley
//
//  Tenant console Workstreams page: roll-up of runs per workstream. A row
//  click opens the Runs page filtered to that workstream.
//
import { useEffect, useState } from 'react'
import { api } from '@shared/api/client'
import { isOk, type Envelope } from '@shared/api/envelope'
import type { Page, Workstream } from '@shared/api/types'
import type { Session } from '@shared/auth'
import { href, navigate } from '@shared/router'
import { ago, EstSpend } from './format'

export function WorkstreamsTable({ items }: { items: Workstream[] }) {
  return (
    <table>
      <thead><tr><th>Workstream</th><th className="rt">Runs</th><th className="rt">Live</th><th>Agents</th><th className="rt">Spend</th><th>Last activity</th></tr></thead>
      <tbody>
        {items.length === 0 && <tr><td colSpan={6} className="hint">No workstreams synced yet.</td></tr>}
        {items.map((w) => (
          <tr key={w.workstream_id} onClick={() => navigate(`/runs?workstream=${encodeURIComponent(w.workstream_id)}`)} style={{ cursor: 'pointer' }}>
            <td className="mono">{w.workstream_id}</td>
            <td className="rt mono">{w.runs}</td>
            <td className="rt mono">{w.live_runs}</td>
            <td>{w.agents.map((a) => <span key={a} className="pill" style={{ marginRight: 4 }}>{a}</span>)}</td>
            <td className="rt"><EstSpend usd={w.totals.usd_est} /></td>
            <td className="hint">{ago(w.last_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export default function Workstreams({ session }: { session: Session }) {
  const [r, setR] = useState<Envelope<Page<Workstream>> | null>(null)
  useEffect(() => {
    setR(null)
    api<Page<Workstream>>('/fleet/workstreams', {}, session.token).then(setR)
  }, [session.token])
  return (
    <>
      <h2>Workstreams</h2>
      <p className="hint">Roll-up of runs by workstream. Click a row for its runs.</p>
      {r === null && <p className="hint">Loading workstreams…</p>}
      {r && !isOk(r) && <p className="err" role="alert">{r.error.code}: {r.error.message}</p>}
      {r && isOk(r) && <WorkstreamsTable items={r.data.items} />}
      <p><a href={href('/runs')}>All runs ›</a></p>
    </>
  )
}
